import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    SparklesIcon,
    XMarkIcon,
    GlobeAltIcon,
    CalendarIcon,
    UserGroupIcon,
    CheckIcon,
    ChevronUpDownIcon
} from '@heroicons/react/24/outline';
import { Combobox, Transition } from '@headlessui/react';
import { usePricing } from '../../contexts/PricingContext';
import { useTheme } from '../../contexts/ThemeContext';
import { PricingTier } from '../../types';
import { countries } from '../../utils/countries';

interface StartPlanningButtonProps {
    label?: string;
    className?: string;
    wrapperClassName?: string;
}

// The modal is rendered via a Portal at document.body to escape any CSS stacking context
const PlanningModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const {
        countryCode,
        tier,
        travelDates,
        pax,
        setCountry,
        setTier,
        setDates,
        setPax
    } = usePricing();
    const { actualTheme } = useTheme();
    const dark = actualTheme === 'dark';
    const [query, setQuery] = useState('');

    const filteredCountries =
        query === ''
            ? countries
            : countries.filter((c) =>
                c.name.toLowerCase().replace(/\s+/g, '').includes(query.toLowerCase().replace(/\s+/g, ''))
            );

    const selectedCountry = countries.find((c) => c.code === countryCode) || countries[0];

    // Prevent body scroll while open
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    // Close on Escape key
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    const panel = (
        <AnimatePresence>
            {/* Full-viewport fixed overlay rendered at body level */}
            <div
                className="fixed inset-0 flex items-center justify-center"
                style={{ zIndex: 9999 }}
            >
                {/* Backdrop – click to close */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="absolute inset-0 bg-black/60 backdrop-blur-md"
                    onClick={onClose}
                    aria-hidden="true"
                />

                {/* Card */}
                <motion.div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Customize Your Safari"
                    initial={{ opacity: 0, scale: 0.94, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.94, y: 20 }}
                    transition={{ type: 'spring', damping: 30, stiffness: 340 }}
                    className={`
                        relative w-[calc(100vw-2rem)] max-w-md mx-auto rounded-3xl
                        border shadow-2xl overflow-hidden
                        ${dark
                            ? 'bg-slate-900/85 border-slate-600/40 text-slate-100'
                            : 'bg-white/85 border-white/60 text-neutral-900'
                        }
                        backdrop-blur-2xl
                    `}
                    style={{ boxShadow: '0 32px 80px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.18)' }}
                    // Stop clicks inside the card from closing the modal
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Top gloss */}
                    <div className="absolute top-0 inset-x-0 h-24 bg-gradient-to-b from-white/12 to-transparent pointer-events-none rounded-t-3xl" />
                    {/* Bottom shimmer */}
                    <div className="absolute bottom-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-primary-400/40 to-transparent" />

                    {/* Scrollable inner area (in case screen is very small) */}
                    <div className="relative overflow-y-auto max-h-[calc(100vh-4rem)] p-6 sm:p-7">

                        {/* ── Header ── */}
                        <div className="flex items-center justify-between mb-5">
                            <div className="flex items-center gap-3">
                                <div className={`p-2.5 rounded-2xl ${dark ? 'bg-primary-900/60 text-primary-400' : 'bg-primary-50 text-primary-600'}`}>
                                    <SparklesIcon className="w-5 h-5 animate-pulse" />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold leading-tight">Customize Your Safari</h3>
                                    <p className={`text-xs mt-0.5 ${dark ? 'text-slate-400' : 'text-neutral-500'}`}>Prices update instantly</p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                aria-label="Close"
                                className={`p-2 rounded-full transition-colors ${dark ? 'hover:bg-slate-700/70 text-slate-400 hover:text-slate-200' : 'hover:bg-neutral-100 text-neutral-500 hover:text-neutral-800'}`}
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>

                        {/* ── Fields stack ── */}
                        <div className="flex flex-col gap-5">

                            {/* ORIGIN & NATIONALITY */}
                            <div>
                                <label className="flex items-center gap-1.5 text-[10px] font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest mb-2">
                                    <GlobeAltIcon className="h-3.5 w-3.5" />
                                    Origin &amp; Nationality
                                </label>
                                <Combobox value={selectedCountry} onChange={(c: any) => c && setCountry(c.code)}>
                                    <div className="relative">
                                        <div className={`relative w-full rounded-xl overflow-hidden border-2 transition-all
                                            ${dark ? 'bg-slate-800 border-slate-700 focus-within:border-primary-500' : 'bg-white border-neutral-200 focus-within:border-primary-400'}`}>
                                            <Combobox.Input
                                                className={`w-full py-3 pl-4 pr-10 text-sm font-semibold bg-transparent border-none focus:ring-0 outline-none
                                                    ${dark ? 'text-neutral-100 placeholder-slate-400' : 'text-neutral-900 placeholder-neutral-400'}`}
                                                displayValue={(country: any) => country?.name}
                                                onChange={(e) => setQuery(e.target.value)}
                                                placeholder="Search country…"
                                            />
                                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3">
                                                <ChevronUpDownIcon className={`h-5 w-5 ${dark ? 'text-slate-400' : 'text-gray-400'}`} />
                                            </Combobox.Button>
                                        </div>
                                        <Transition as={React.Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                                            <Combobox.Options
                                                className={`absolute mt-2 max-h-52 w-full overflow-auto rounded-xl py-1.5 text-sm shadow-xl ring-1 ring-black/10 z-10
                                                ${dark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-neutral-200'}`}
                                            >
                                                {filteredCountries.length === 0 && query !== '' ? (
                                                    <div className={`py-3 px-4 ${dark ? 'text-slate-400' : 'text-gray-500'}`}>Nothing found.</div>
                                                ) : (
                                                    filteredCountries.map((country) => (
                                                        <Combobox.Option
                                                            key={country.code}
                                                            value={country}
                                                            className={({ active }) =>
                                                                `relative cursor-pointer select-none py-2.5 pl-10 pr-4 mx-1.5 rounded-lg transition-colors
                                                                ${active ? (dark ? 'bg-slate-700 text-white' : 'bg-primary-50 text-primary-900') : (dark ? 'text-neutral-100' : 'text-gray-900')}`
                                                            }
                                                        >
                                                            {({ selected, active }) => (
                                                                <>
                                                                    <span className={`block truncate ${selected ? 'font-bold' : 'font-medium'}`}>
                                                                        {country.flag} {country.name}
                                                                    </span>
                                                                    {selected && (
                                                                        <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? (dark ? 'text-white' : 'text-primary-600') : 'text-primary-500'}`}>
                                                                            <CheckIcon className="h-4 w-4" />
                                                                        </span>
                                                                    )}
                                                                </>
                                                            )}
                                                        </Combobox.Option>
                                                    ))
                                                )}
                                            </Combobox.Options>
                                        </Transition>
                                    </div>
                                </Combobox>
                                <p className={`mt-1.5 text-[10px] text-center font-medium italic ${dark ? 'text-slate-500' : 'text-neutral-400'}`}>
                                    *Local rates apply for Kenyan Residents
                                </p>
                            </div>

                            {/* EXPERIENCE TIER */}
                            <div>
                                <label className="flex items-center gap-1.5 text-[10px] font-bold text-accent-600 dark:text-accent-400 uppercase tracking-widest mb-2">
                                    <SparklesIcon className="h-3.5 w-3.5" />
                                    Experience Tier
                                </label>
                                <div className="flex gap-2">
                                    {Object.values(PricingTier).map((t) => (
                                        <label
                                            key={t}
                                            className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-xl cursor-pointer transition-all border-2 select-none
                                                ${tier === t
                                                    ? dark ? 'bg-slate-800 border-primary-500 shadow-sm' : 'bg-white border-primary-500 shadow-sm'
                                                    : dark ? 'bg-slate-800/50 border-transparent hover:border-slate-600' : 'bg-neutral-50 border-transparent hover:border-neutral-300'
                                                }`}
                                        >
                                            <input
                                                type="radio"
                                                name="planning-modal-tier"
                                                value={t}
                                                checked={tier === t}
                                                onChange={() => setTier(t)}
                                                className="w-3.5 h-3.5 text-primary-600 focus:ring-primary-500"
                                            />
                                            <span className={`text-[11px] font-bold ${tier === t ? (dark ? 'text-primary-400' : 'text-primary-700') : (dark ? 'text-slate-400' : 'text-neutral-500')}`}>
                                                {t}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                                <p className={`mt-1.5 text-[10px] text-center font-medium ${dark ? 'text-slate-500' : 'text-neutral-400'}`}>
                                    {tier === 'LUXURY' && '✨ 5-Star Lodges & Senior Guides'}
                                    {tier === 'STANDARD' && '🐘 Premium Camps & Expert Guides'}
                                    {tier === 'BUDGET' && '⛺ Adventure Tents & Local Guides'}
                                </p>
                            </div>

                            {/* TRAVEL DATE + GROUP COMPOSITION side-by-side on wider screens */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {/* Travel Date */}
                                <div>
                                    <label className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest mb-2">
                                        <CalendarIcon className="h-3.5 w-3.5" />
                                        Travel Date
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="date"
                                            value={travelDates?.startDate || ''}
                                            onChange={(e) => setDates(e.target.value, travelDates?.endDate || '')}
                                            className={`w-full pl-3.5 pr-9 py-3 border-2 rounded-xl text-sm font-semibold appearance-none focus:ring-2 focus:ring-primary-500 transition-all cursor-pointer
                                                ${dark ? 'bg-slate-800 border-slate-700 text-neutral-100 hover:border-primary-500' : 'bg-white border-neutral-200 text-neutral-800 hover:border-primary-400'}`}
                                        />
                                        <div className={`absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${dark ? 'text-slate-500' : 'text-neutral-400'}`}>
                                            <CalendarIcon className="h-4 w-4" />
                                        </div>
                                    </div>
                                </div>

                                {/* Group Composition */}
                                <div>
                                    <label className="flex items-center gap-1.5 text-[10px] font-bold text-orange-600 dark:text-orange-400 uppercase tracking-widest mb-2">
                                        <UserGroupIcon className="h-3.5 w-3.5" />
                                        Group
                                    </label>
                                    <div className={`flex items-center gap-3 border-2 rounded-xl px-4 py-2.5
                                        ${dark ? 'bg-slate-800 border-slate-700' : 'bg-white border-neutral-200'}`}>
                                        <div className="flex-1 flex flex-col items-center">
                                            <span className={`text-[9px] font-bold uppercase mb-0.5 ${dark ? 'text-slate-400' : 'text-neutral-400'}`}>Adults</span>
                                            <input
                                                type="number"
                                                min="1"
                                                className={`w-full bg-transparent border-none text-center text-sm font-bold focus:ring-0 p-0 ${dark ? 'text-neutral-100' : 'text-neutral-800'}`}
                                                value={pax.adults}
                                                onChange={(e) => setPax(Math.max(1, parseInt(e.target.value) || 1), pax.children)}
                                            />
                                        </div>
                                        <div className={`w-px h-8 self-center ${dark ? 'bg-slate-700' : 'bg-neutral-200'}`} />
                                        <div className="flex-1 flex flex-col items-center">
                                            <span className={`text-[9px] font-bold uppercase mb-0.5 ${dark ? 'text-slate-400' : 'text-neutral-400'}`}>Children</span>
                                            <input
                                                type="number"
                                                min="0"
                                                className={`w-full bg-transparent border-none text-center text-sm font-bold focus:ring-0 p-0 ${dark ? 'text-neutral-100' : 'text-neutral-800'}`}
                                                value={pax.children}
                                                onChange={(e) => setPax(pax.adults, Math.max(0, parseInt(e.target.value) || 0))}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Apply button */}
                        <button
                            onClick={onClose}
                            className="mt-6 w-full py-3.5 rounded-2xl font-bold text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 shadow-lg shadow-primary-500/30 transition-all active:scale-[0.98]"
                        >
                            Apply &amp; See Updated Prices
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );

    return ReactDOM.createPortal(panel, document.body);
};


/**
 * Compact "Start Planning" trigger button.
 * Opens a floating liquid-glass modal via a React Portal – rendering at document.body
 * so it is never trapped inside a CSS stacking context.
 */
const StartPlanningButton: React.FC<StartPlanningButtonProps> = ({
    label = 'Start Planning',
    className,
    wrapperClassName
}) => {
    const { actualTheme } = useTheme();
    const dark = actualTheme === 'dark';

    const [isOpen, setIsOpen] = useState(false);
    const [showBriefing, setShowBriefing] = useState(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    // Show briefing tooltip once per session for 5 s
    useEffect(() => {
        const alreadyShown = sessionStorage.getItem('start_planning_briefing_shown');
        if (!alreadyShown) {
            const delay = setTimeout(() => {
                setShowBriefing(true);
                sessionStorage.setItem('start_planning_briefing_shown', '1');
                timerRef.current = setTimeout(() => setShowBriefing(false), 5000);
            }, 700);
            return () => {
                clearTimeout(delay);
                if (timerRef.current) clearTimeout(timerRef.current);
            };
        }
    }, []);

    const handleOpen = () => {
        setShowBriefing(false);
        if (timerRef.current) clearTimeout(timerRef.current);
        setIsOpen(true);
    };

    return (
        <>
            <div className={`relative inline-block ${wrapperClassName || ''}`}>
                {/* Briefing tooltip */}
                <AnimatePresence>
                    {showBriefing && (
                        <motion.div
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.25 }}
                            className={`absolute top-full mt-4 right-0 w-56 rounded-2xl shadow-2xl border p-4 z-50
                                ${dark ? 'bg-slate-800 border-primary-700/50 text-slate-200' : 'bg-white border-primary-200 text-primary-900'}`}
                        >
                            {/* Arrow */}
                            <div className={`absolute -top-2 right-5 w-4 h-4 rotate-45 border-l border-t
                                ${dark ? 'bg-slate-800 border-primary-700/50' : 'bg-white border-primary-200'}`} />
                            <button
                                onClick={(e) => { e.stopPropagation(); setShowBriefing(false); }}
                                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-lg">🗺️</span>
                                <h4 className="font-bold text-sm">Personalise Your Safari</h4>
                            </div>
                            <p className={`text-xs leading-relaxed ${dark ? 'text-slate-400' : 'text-neutral-600'}`}>
                                Set residency, tier &amp; group size for real-time pricing.
                            </p>
                            <button
                                onClick={(e) => { e.stopPropagation(); handleOpen(); }}
                                className="mt-3 w-full py-1.5 rounded-lg text-xs font-bold bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                            >
                                Let's go →
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>

                <button
                    id="start-planning-btn"
                    onClick={handleOpen}
                    className={`${className || 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-bold text-sm bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg shadow-primary-500/30 hover:shadow-primary-500/50 hover:scale-105 active:scale-95'} transition-all duration-200 select-none whitespace-nowrap ${showBriefing ? 'ring-2 ring-primary-400 ring-offset-2 animate-pulse' : ''}`}
                    aria-label="Open planning modal"
                >
                    <SparklesIcon className="h-4 w-4 flex-shrink-0" />
                    {label}
                </button>
            </div>

            {/* Portal modal — renders at document.body, escaping all stacking contexts */}
            {isOpen && <PlanningModal onClose={() => setIsOpen(false)} />}
        </>
    );
};

export default StartPlanningButton;
